import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { collection, addDoc, serverTimestamp, doc, updateDoc, increment } from 'firebase/firestore';
import './Payment.css';

const Payment = () => {
    const { currentUser, userData } = useAuth();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [transactionId, setTransactionId] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [statusCheckInterval, setStatusCheckInterval] = useState(null);
    
    const [formData, setFormData] = useState({
        phone: '',
        amount: '',
        category: '',
        remarks: '',
        projectId: '' // Add projectId for project contributions
    });

    const amountSuggestions = [100, 200, 500, 1000, 2000];

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (statusCheckInterval) {
                clearInterval(statusCheckInterval);
            }
        };
    }, [statusCheckInterval]);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleAmountSuggestion = (amount) => {
        setFormData(prev => ({
            ...prev,
            amount: amount.toString()
        }));
    };

    const handleCategoryChange = (e) => {
        const category = e.target.value;
        let amount = formData.amount;

        if (category === 'social_lunch') {
            amount = '400';
        } else if (category === 'sem_subscription') {
            amount = '100';
        }

        setFormData(prev => ({
            ...prev,
            category,
            amount,
            projectId: '' // Reset projectId when category changes
        }));
    };

    const formatPhoneNumber = (value) => {
        let phone = value.replace(/\D/g, '');
        if (phone.length > 0) {
            if (phone.startsWith('0')) {
                phone = '254' + phone.substring(1);
            } else if (phone.startsWith('7')) {
                phone = '254' + phone;
            } else if (!phone.startsWith('254')) {
                phone = '254' + phone;
            }
            if (phone.length > 12) {
                phone = phone.substring(0, 12);
            }
        }
        return phone;
    };

    const validateForm = () => {
        const phoneRegex = /^254[0-9]{9}$/;
        if (!phoneRegex.test(formData.phone)) {
            showToast('Please enter a valid phone number in format 254XXXXXXXXX', 'error');
            return false;
        }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            showToast('Please enter a valid amount', 'error');
            return false;
        }

        if (!formData.category) {
            showToast('Please select a contribution category', 'error');
            return false;
        }

        return true;
    };

    // Check transaction status
    const checkTransactionStatus = async (transactionId) => {
        try {
            const response = await fetch('/.netlify/functions/check-payment-status', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ transactionId })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                if (result.data.status === 'completed') {
                    // Transaction completed successfully
                    clearInterval(statusCheckInterval);
                    setCheckingStatus(false);
                    setPaymentStatus('completed');
                    
                    // Update transaction in Firestore
                    await updateDoc(doc(db, 'transactions', transactionId), {
                        status: 'completed',
                        completedAt: serverTimestamp(),
                        mpesaReceipt: result.data.mpesaReceipt
                    });

                    // Update source totals if applicable
                    if (formData.category && formData.projectId) {
                        const sourceRef = doc(db, 'incomeSources', formData.projectId);
                        await updateDoc(sourceRef, {
                            totalIncome: increment(parseFloat(formData.amount)),
                            transactionCount: increment(1)
                        });
                    }

                    showToast('Payment completed successfully! Thank you for your contribution.', 'success');
                    
                    // Reset form
                    setFormData({
                        phone: '',
                        amount: '',
                        category: '',
                        remarks: '',
                        projectId: ''
                    });
                    setTransactionId(null);

                } else if (result.data.status === 'failed') {
                    // Transaction failed
                    clearInterval(statusCheckInterval);
                    setCheckingStatus(false);
                    setPaymentStatus('failed');
                    
                    await updateDoc(doc(db, 'transactions', transactionId), {
                        status: 'failed',
                        failedAt: serverTimestamp(),
                        failureReason: result.data.failureReason || 'Payment failed'
                    });

                    showToast('Payment failed. Please try again.', 'error');
                }
            }
        } catch (error) {
            console.error('Error checking transaction status:', error);
            showToast('Error checking payment status', 'error');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setPaymentStatus(null);

        try {
            // Create transaction data - ensure no undefined values
            const transactionData = {
                userId: currentUser?.uid || 'anonymous',
                userName: userData?.fullName || 'Anonymous',
                userEmail: currentUser?.email || '',
                phone: formData.phone,
                amount: parseFloat(formData.amount),
                category: formData.category,
                remarks: formData.remarks || '',
                status: 'initiated',
                createdAt: serverTimestamp(),
                paymentMethod: 'mpesa',
                // Only include sourceId if projectId exists
                ...(formData.projectId && { sourceId: formData.projectId }),
                // Only include projectId if it exists
                ...(formData.projectId && { projectId: formData.projectId })
            };

            console.log('Creating transaction:', transactionData);

            const transactionRef = await addDoc(collection(db, 'contributions'), transactionData);
            const newTransactionId = transactionRef.id;
            
            console.log('Transaction created with ID:', newTransactionId);

            // Initiate STK push via Netlify Function
            const response = await fetch('/.netlify/functions/initiate-payment', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    phoneNumber: formData.phone,      // Match the parameter name in function
    amount: parseFloat(formData.amount),
    accountType: formData.category,   // This is accountReference
    description: formData.remarks || `Payment for ${formData.category}`,
    userId: currentUser?.uid,
    userName: userData?.fullName || 'Anonymous',
    transactionId: newTransactionId    // Add this
  })
});


            if (!response.ok) {
                const errorText = await response.text();
                console.error('Server response:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('Payment initiation result:', result);

            if (result.success) {
                setTransactionId(newTransactionId);
                setCheckingStatus(true);
                setPaymentStatus('processing');
                
                showToast('STK push sent! Please check your phone and enter your PIN.', 'info');

                // Start checking transaction status every 3 seconds
                const interval = setInterval(() => {
                    checkTransactionStatus(newTransactionId);
                }, 3000);
                
                setStatusCheckInterval(interval);

                // Stop checking after 60 seconds
                setTimeout(() => {
                    if (interval) {
                        clearInterval(interval);
                        setCheckingStatus(false);
                        if (paymentStatus === 'processing') {
                            setPaymentStatus('timeout');
                            showToast('Payment timeout. Please check transaction status in your statements.', 'warning');
                        }
                    }
                }, 60000);

            } else {
                // Update transaction as failed
                await updateDoc(doc(db, 'transactions', newTransactionId), {
                    status: 'failed',
                    failedAt: serverTimestamp(),
                    failureReason: result.error || 'Payment initiation failed'
                });
                
                showToast(result.error || 'Payment initiation failed', 'error');
                setLoading(false);
            }

        } catch (error) {
            console.error('Error processing contribution:', error);
            showToast(`Error: ${error.message}`, 'error');
            setLoading(false);
        }
    };

    const getCategoryLabel = (category) => {
        const categories = {
            'social_lunch': 'Social Lunch',
            'transport': 'Transport',
            'development': 'Development',
            'donation': 'Donation',
            'mission': 'Mission Support',
            'sem_subscription': 'SEM Subscription',
            'project': 'Project',
            'other': 'Other'
        };
        return categories[category] || category;
    };

    return (
        <div className="payment-page">
            <h1 className="payment-page-title">Make a Contribution</h1>

            {/* Payment Status Display */}
            {paymentStatus === 'processing' && (
                <div className="payment-status processing">
                    <i className="fas fa-spinner fa-spin"></i>
                    <div className="payment-status-content">
                        <h3>Processing Payment</h3>
                        <p>Please check your phone and enter your M-Pesa PIN to complete the transaction.</p>
                        <div className="payment-status-details">
                            <p><strong>Amount:</strong> KES {formData.amount}</p>
                            <p><strong>Phone:</strong> {formData.phone}</p>
                            <p><strong>Category:</strong> {getCategoryLabel(formData.category)}</p>
                        </div>
                    </div>
                </div>
            )}

            {paymentStatus === 'completed' && (
                <div className="payment-status completed">
                    <i className="fas fa-check-circle"></i>
                    <div className="payment-status-content">
                        <h3>Payment Successful!</h3>
                        <p>Thank you for your contribution. God bless you abundantly.</p>
                    </div>
                </div>
            )}

            {paymentStatus === 'failed' && (
                <div className="payment-status failed">
                    <i className="fas fa-times-circle"></i>
                    <div className="payment-status-content">
                        <h3>Payment Failed</h3>
                        <p>The transaction was not completed. Please try again.</p>
                    </div>
                </div>
            )}

            {paymentStatus === 'timeout' && (
                <div className="payment-status timeout">
                    <i className="fas fa-clock"></i>
                    <div className="payment-status-content">
                        <h3>Payment Timeout</h3>
                        <p>The transaction took too long. Please check your M-Pesa statements and try again if not deducted.</p>
                    </div>
                </div>
            )}

            <div className="payment-container">
                {/* Payment Form */}
                <div className="payment-form-container">
                    <form onSubmit={handleSubmit} className="payment-form">
                        <div className="payment-form-group">
                            <label htmlFor="phone">
                                <i className="fas fa-phone"></i> M-Pesa Phone Number
                            </label>
                            <input
                                type="tel"
                                id="phone"
                                value={formData.phone}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    phone: formatPhoneNumber(e.target.value)
                                }))}
                                placeholder="254XXXXXXXXX"
                                required
                                disabled={checkingStatus}
                            />
                            <small className="payment-hint">The M-Pesa registered phone number</small>
                        </div>

                        <div className="payment-form-group">
                            <label htmlFor="amount">
                                <i className="fas fa-money-bill-wave"></i> Amount (KES)
                            </label>
                            <input
                                type="number"
                                id="amount"
                                value={formData.amount}
                                onChange={handleChange}
                                placeholder="Enter amount"
                                min="1"
                                required
                                disabled={checkingStatus}
                            />
                            
                            <div className="payment-amount-suggestions">
                                {amountSuggestions.map(amount => (
                                    <button
                                        key={amount}
                                        type="button"
                                        className={`payment-amount-btn ${formData.amount === amount.toString() ? 'active' : ''}`}
                                        onClick={() => handleAmountSuggestion(amount)}
                                        disabled={checkingStatus}
                                    >
                                        {amount.toLocaleString()}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="payment-form-group">
                            <label htmlFor="category">
                                <i className="fas fa-tag"></i> Contribution Category
                            </label>
                            <select
                                id="category"
                                value={formData.category}
                                onChange={handleCategoryChange}
                                required
                                disabled={checkingStatus}
                            >
                                <option value="">-- Select Category --</option>
                                <option value="social_lunch">Social Lunch (400)</option>
                                <option value="transport">Transport</option>
                                <option value="development">Development</option>
                                <option value="donation">Donation</option>
                                <option value="mission">Mission Support</option>
                                <option value="sem_subscription">Sem Subscription (100)</option>
                                <option value="project">Project</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        <div className="payment-form-group">
                            <label htmlFor="remarks">
                                <i className="fas fa-comment"></i> Remarks (Optional)
                            </label>
                            <textarea
                                id="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                placeholder="Brief description of your contribution"
                                rows="4"
                                disabled={checkingStatus}
                            ></textarea>
                        </div>

                        <button
                            type="submit"
                            className="payment-submit-btn"
                            disabled={loading || checkingStatus}
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Initiating...
                                </>
                            ) : checkingStatus ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-money-bill-wave"></i>
                                    Pay with M-Pesa
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Payment Information */}
                <div className="payment-info-container">
                    <div className="payment-info-card">
                        <h3>
                            <i className="fas fa-info-circle"></i>
                            Contribution Information
                        </h3>

                        <ul className="payment-info-list">
                            <li>
                                <i className="fas fa-check-circle"></i>
                                All contributions support the ministry's activities and missions
                            </li>
                            <li>
                                <i className="fas fa-check-circle"></i>
                                You will receive an M-Pesa confirmation message
                            </li>
                            <li>
                                <i className="fas fa-check-circle"></i>
                                Your contribution details are automatically recorded
                            </li>
                            <li>
                                <i className="fas fa-check-circle"></i>
                                Receipts available upon request
                            </li>
                        </ul>

                        <div className="payment-mpesa-info">
                            <h4>
                                <i className="fab fa-mpesa"></i>
                                How it works
                            </h4>
                            <ol className="payment-steps">
                                <li>Enter your M-Pesa registered phone number</li>
                                <li>Enter the amount you wish to contribute</li>
                                <li>Select the contribution category</li>
                                <li>Click "Pay with M-Pesa"</li>
                                <li>Check your phone and enter your M-Pesa PIN</li>
                                <li>Wait for confirmation</li>
                            </ol>
                            <p className="payment-note">
                                <i className="fas fa-shield-alt"></i>
                                Secure payment processed via Lipana.dev
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <footer className="payment-footer">
                <p>White Doves Ministry &copy; {new Date().getFullYear()}. All contributions support our mission work.</p>
            </footer>
        </div>
    );
};

export default Payment;
